import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, DollarSign, Package, TrendingUp, X, Edit2, Trash2, Ban, CheckCircle, Shield, Key } from 'lucide-react'
import { adminAPI, developerApplicationAPI } from '../services/api'
import { useModalClose } from '../hooks/useModalClose'
import DashboardHeader from '../components/DashboardHeader'
import DashboardFooter from '../components/DashboardFooter'
import toast from 'react-hot-toast'

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'admins' | 'billing' | 'subscriptions' | 'apps' | 'dev-applications' | 'coupons' | 'analytics' | 'system'>('overview')
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedAdminId, setSelectedAdminId] = useState<number | null>(null)
  const [showRejectModal, setShowRejectModal] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const queryClient = useQueryClient()

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['admin-analytics'],
    queryFn: async () => {
      const { data } = await adminAPI.getAnalytics()
      return data
    },
  })

  const { data: users, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data } = await adminAPI.getUsers()
      console.log('Admin users response:', data)
      // Filter out admins from regular users list
      return {
        ...data,
        users: data.users?.filter((u: any) => u.role !== 'admin') || []
      }
    },
    enabled: activeTab === 'users',
    retry: 1,
  })

  const { data: admins, isLoading: adminsLoading, error: adminsError } = useQuery({
    queryKey: ['admin-admins'],
    queryFn: async () => {
      const { data } = await adminAPI.getAdmins()
      console.log('Admin admins response:', data)
      return data
    },
    enabled: activeTab === 'admins',
    retry: 1,
  })

  const { data: pendingApps, isLoading: appsLoading } = useQuery({
    queryKey: ['admin-pending-apps'],
    queryFn: async () => {
      const { data } = await adminAPI.getPendingApps()
      return data
    },
    enabled: activeTab === 'apps',
  })

  // User details query
  const { data: selectedUser, isLoading: userLoading } = useQuery({
    queryKey: ['admin-user', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return null
      const { data } = await adminAPI.getUser(selectedUserId)
      return data
    },
    enabled: !!selectedUserId,
  })

  // User management mutations
  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      adminAPI.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin-user', selectedUserId] })
      toast.success('User updated successfully')
    },
    onError: () => toast.error('Failed to update user'),
  })

  const suspendUserMutation = useMutation({
    mutationFn: (id: number) => adminAPI.suspendUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin-user', selectedUserId] })
      toast.success('User suspended')
    },
    onError: () => toast.error('Failed to suspend user'),
  })

  const activateUserMutation = useMutation({
    mutationFn: (id: number) => adminAPI.activateUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      queryClient.invalidateQueries({ queryKey: ['admin-user', selectedUserId] })
      toast.success('User activated')
    },
    onError: () => toast.error('Failed to activate user'),
  })

  const deleteUserMutation = useMutation({
    mutationFn: (id: number) => adminAPI.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setSelectedUserId(null)
      toast.success('User deleted')
    },
    onError: () => toast.error('Failed to delete user'),
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, newPassword }: { id: number; newPassword: string }) => 
      adminAPI.resetUserPassword(id, newPassword),
    onSuccess: () => {
      toast.success('Password reset successfully')
    },
    onError: () => toast.error('Failed to reset password'),
  })

  // Admin details query
  const { data: selectedAdmin, isLoading: adminLoading } = useQuery({
    queryKey: ['admin-admin', selectedAdminId],
    queryFn: async () => {
      if (!selectedAdminId) return null
      const { data } = await adminAPI.getUser(selectedAdminId)
      return data
    },
    enabled: !!selectedAdminId,
  })

  // Admin management mutations
  const createAdminMutation = useMutation({
    mutationFn: (data: any) => adminAPI.createAdmin(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-admins'] })
      toast.success('Admin created successfully')
    },
    onError: () => toast.error('Failed to create admin'),
  })

  const updateAdminMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      adminAPI.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-admins'] })
      queryClient.invalidateQueries({ queryKey: ['admin-admin', selectedAdminId] })
      toast.success('Admin updated successfully')
    },
    onError: () => toast.error('Failed to update admin'),
  })

  const deleteAdminMutation = useMutation({
    mutationFn: (id: number) => adminAPI.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-admins'] })
      setSelectedAdminId(null)
      toast.success('Admin deleted')
    },
    onError: () => toast.error('Failed to delete admin'),
  })

  // App moderation mutations
  const approveAppMutation = useMutation({
    mutationFn: (id: number) => adminAPI.approveApp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pending-apps'] })
      toast.success('App approved successfully')
    },
    onError: () => toast.error('Failed to approve app'),
  })

  const rejectAppMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => 
      adminAPI.rejectApp(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pending-apps'] })
      setShowRejectModal(null)
      setRejectReason('')
      toast.success('App rejected')
    },
    onError: () => toast.error('Failed to reject app'),
  })

  // Developer Applications
  const { data: devApplications, isLoading: devAppLoading } = useQuery({
    queryKey: ['admin-dev-applications'],
    queryFn: async () => {
      const { data } = await developerApplicationAPI.getAll()
      return data
    },
    enabled: activeTab === 'dev-applications',
  })

  const approveDeveloperMutation = useMutation({
    mutationFn: (applicationId: number) => 
      developerApplicationAPI.approve(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-dev-applications'] })
      toast.success('Application approved! User is now a developer')
    },
    onError: () => toast.error('Failed to approve application'),
  })

  const rejectDeveloperMutation = useMutation({
    mutationFn: ({ applicationId, reason }: { applicationId: number; reason: string }) => 
      developerApplicationAPI.reject(applicationId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-dev-applications'] })
      setShowRejectModal(null)
      setRejectReason('')
      toast.success('Application rejected')
    },
    onError: () => toast.error('Failed to reject application'),
  })

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'admins', label: 'Admins' },
    { id: 'billing', label: 'Billing' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'apps', label: 'App Moderation' },
    { id: 'dev-applications', label: 'Developer Applications' },
    { id: 'coupons', label: 'Coupons' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'system', label: 'System Administration' },
  ]

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <DashboardHeader />

      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Admin Panel</h1>

        <div className="flex gap-4 mb-8 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-6 py-3 rounded-lg font-medium transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary-600 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div>
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : analytics?.users ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-600 font-medium">Total Users</h3>
                    <Users className="text-primary-600" size={24} />
                  </div>
                  <div className="text-3xl font-bold text-gray-900">{analytics.users.total || 0}</div>
                  <div className="text-sm text-gray-500 mt-2">{analytics.users.active || 0} active</div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-600 font-medium">Total Revenue</h3>
                    <DollarSign className="text-green-600" size={24} />
                  </div>
                  <div className="text-3xl font-bold text-gray-900">
                    ${(analytics.revenue?.total || 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    ${(analytics.revenue?.monthly || 0).toLocaleString()} this month
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-600 font-medium">Dashboards</h3>
                    <Package className="text-blue-600" size={24} />
                  </div>
                  <div className="text-3xl font-bold text-gray-900">{analytics.dashboards || 0}</div>
                  <div className="text-sm text-gray-500 mt-2">{analytics.widgets || 0} total widgets</div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-gray-600 font-medium">Apps</h3>
                    <TrendingUp className="text-purple-600" size={24} />
                  </div>
                  <div className="text-3xl font-bold text-gray-900">{analytics.apps || 0}</div>
                  <div className="text-sm text-gray-500 mt-2">Approved apps</div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No analytics data available
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : usersError ? (
              <div className="text-center py-12">
                <p className="text-red-600 mb-2">Error loading users</p>
                <p className="text-sm text-gray-500">{(usersError as any)?.message || 'Failed to fetch users'}</p>
              </div>
            ) : users?.users && Array.isArray(users.users) && users.users.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.users.map((user: any) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : 'No name set'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs bg-primary-100 text-primary-800 rounded-full">
                          {user.plan_type || 'free'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            user.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {user.is_active ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button 
                          onClick={() => setSelectedUserId(user.id)}
                          className="text-primary-600 hover:text-primary-900 font-medium"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No users found
              </div>
            )}
          </div>
        )}

        {activeTab === 'admins' && (
          <div className="space-y-6">
            {/* Create Admin Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Admin Management</h2>
                <p className="text-gray-600 mt-1">Manage admin accounts with elevated permissions</p>
              </div>
              <button
                onClick={() => setSelectedAdminId(-1)} // -1 indicates creating new admin
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                <Shield size={18} />
                Create Admin
              </button>
            </div>

            {/* Admins Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {adminsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              ) : adminsError ? (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-2">Error loading admins</p>
                  <p className="text-sm text-gray-500">{(adminsError as any)?.message || 'Failed to fetch admins'}</p>
                </div>
              ) : admins?.users && Array.isArray(admins.users) && admins.users.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Admin
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {admins.users.map((admin: any) => (
                      <tr key={admin.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Shield size={16} className="text-red-600" />
                            <span className="font-medium text-gray-900">
                              {admin.first_name && admin.last_name ? `${admin.first_name} ${admin.last_name}` : 'No name set'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{admin.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                          {admin.last_login ? new Date(admin.last_login).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              admin.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {admin.is_active ? 'Active' : 'Suspended'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button 
                            onClick={() => setSelectedAdminId(admin.id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Manage
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Shield size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No admin accounts found</p>
                  <p className="text-sm mt-2">Create your first admin account to get started</p>
                </div>
              )}
            </div>

            {/* Warning Box */}
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Shield size={20} className="text-red-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-1">Admin Security Notice</h4>
                  <p className="text-sm text-red-700">
                    Admin accounts have full system access. Only create admin accounts for trusted individuals.
                    Regular users cannot be upgraded to admin through the user management interface for security reasons.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : analytics?.revenue ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-gray-600 font-medium mb-2">Monthly Revenue</h3>
                    <div className="text-3xl font-bold text-green-600">
                      ${(analytics.revenue.monthly || 0).toLocaleString()}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">This month</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-gray-600 font-medium mb-2">Total Revenue</h3>
                    <div className="text-3xl font-bold text-blue-600">
                      ${(analytics.revenue.total || 0).toLocaleString()}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">All time</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-gray-600 font-medium mb-2">Total Users</h3>
                    <div className="text-3xl font-bold text-orange-600">{analytics.users?.total || 0}</div>
                    <p className="text-sm text-gray-500 mt-1">{analytics.users?.active || 0} active</p>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Transactions</h3>
                  <div className="text-center py-8 text-gray-500">
                    <p>No transactions yet</p>
                    <p className="text-sm mt-2">Transactions will appear here once users make purchases</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No billing data available
              </div>
            )}
          </div>
        )}

        {activeTab === 'subscriptions' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Subscription Plans</h2>
              <button 
                onClick={() => toast('Subscription plan management coming soon', { icon: '🚀' })}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                Create New Plan
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Free</h3>
                <div className="text-3xl font-bold text-gray-900 mb-4">$0<span className="text-sm font-normal text-gray-500">/month</span></div>
                <ul className="space-y-2 mb-6">
                  <li className="text-sm text-gray-600">✓ 1 Dashboard</li>
                  <li className="text-sm text-gray-600">✓ 5 Widgets</li>
                  <li className="text-sm text-gray-600">✓ Basic Support</li>
                </ul>
                <button 
                  onClick={() => toast('Plan editing coming soon', { icon: '📝' })}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Edit Plan
                </button>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border-2 border-primary-600">
                <div className="inline-block px-2 py-1 bg-primary-100 text-primary-800 rounded text-xs font-medium mb-2">Popular</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Pro</h3>
                <div className="text-3xl font-bold text-gray-900 mb-4">$29<span className="text-sm font-normal text-gray-500">/month</span></div>
                <ul className="space-y-2 mb-6">
                  <li className="text-sm text-gray-600">✓ Unlimited Dashboards</li>
                  <li className="text-sm text-gray-600">✓ Unlimited Widgets</li>
                  <li className="text-sm text-gray-600">✓ Priority Support</li>
                  <li className="text-sm text-gray-600">✓ Custom Themes</li>
                </ul>
                <button 
                  onClick={() => toast('Plan editing coming soon', { icon: '📝' })}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                  Edit Plan
                </button>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Enterprise</h3>
                <div className="text-3xl font-bold text-gray-900 mb-4">$99<span className="text-sm font-normal text-gray-500">/month</span></div>
                <ul className="space-y-2 mb-6">
                  <li className="text-sm text-gray-600">✓ Everything in Pro</li>
                  <li className="text-sm text-gray-600">✓ Team Collaboration</li>
                  <li className="text-sm text-gray-600">✓ Advanced Analytics</li>
                  <li className="text-sm text-gray-600">✓ Dedicated Support</li>
                </ul>
                <button 
                  onClick={() => toast('Plan editing coming soon', { icon: '📝' })}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Edit Plan
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'apps' && (
          <div className="space-y-4">
            {appsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : pendingApps && pendingApps.length > 0 ? (
              pendingApps.map((app: any) => (
                <div key={app.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-3xl">📦</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 mb-1">{app.name}</h3>
                        <p className="text-gray-600 text-sm mb-2">{app.description}</p>
                        <div className="flex gap-2">
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                            {app.category}
                          </span>
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                            by {app.developer_username}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => approveAppMutation.mutate(app.id)}
                        disabled={approveAppMutation.isPending}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button 
                        onClick={() => setShowRejectModal(app.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white p-12 rounded-xl shadow-sm border border-gray-200 text-center">
                <Package size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Apps</h3>
                <p className="text-gray-600">All apps have been reviewed</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'coupons' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Coupon Management</h2>
              <button 
                onClick={() => toast('Coupon creation coming soon', { icon: '🎟️' })}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
              >
                Create Coupon
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-12 text-center">
                <Package size={48} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Coupon System Coming Soon</h3>
                <p className="text-gray-600">Create and manage discount coupons for your users</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'dev-applications' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Developer Applications</h2>
              <p className="text-gray-600 mt-1">Approve or deny user requests for developer access</p>
            </div>

            {devAppLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Experience</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Links</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {devApplications && Array.isArray(devApplications) && devApplications.length > 0 ? (
                      devApplications.map((app: any) => (
                        <tr key={app.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-gray-900">{app.username}</div>
                              <div className="text-sm text-gray-500">{app.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate" title={app.reason}>
                              {app.reason}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate" title={app.experience}>
                              {app.experience || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              {app.portfolio_url && (
                                <a
                                  href={app.portfolio_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  Portfolio
                                </a>
                              )}
                              {app.github_url && (
                                <a
                                  href={app.github_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  GitHub
                                </a>
                              )}
                              {!app.portfolio_url && !app.github_url && '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              app.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              app.status === 'approved' ? 'bg-green-100 text-green-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {app.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {app.status === 'pending' ? (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => approveDeveloperMutation.mutate(app.id)}
                                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => {
                                    setShowRejectModal(app.id)
                                    setRejectReason('')
                                  }}
                                  className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition"
                                >
                                  Reject
                                </button>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500">
                                {app.reviewed_by_username && `By ${app.reviewed_by_username}`}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                          No developer applications yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Site Analytics</h2>
              <p className="text-gray-600 mt-1">Track system usage and performance metrics</p>
            </div>

            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : analytics ? (
              <>
                {/* Real Analytics Data */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-gray-600 font-medium mb-2">Total Users</h3>
                    <div className="text-3xl font-bold text-gray-900">{analytics.users?.total || 0}</div>
                    <p className="text-sm text-gray-500 mt-1">{analytics.users?.active || 0} active users</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-gray-600 font-medium mb-2">Total Dashboards</h3>
                    <div className="text-3xl font-bold text-gray-900">{analytics.dashboards || 0}</div>
                    <p className="text-sm text-gray-500 mt-1">Created by users</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-gray-600 font-medium mb-2">Total Widgets</h3>
                    <div className="text-3xl font-bold text-gray-900">{analytics.widgets || 0}</div>
                    <p className="text-sm text-gray-500 mt-1">In use</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-gray-600 font-medium mb-2">Approved Apps</h3>
                    <div className="text-3xl font-bold text-gray-900">{analytics.apps || 0}</div>
                    <p className="text-sm text-gray-500 mt-1">In app store</p>
                  </div>
                </div>

                {/* Revenue Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Overview</h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-gray-600">Total Revenue</span>
                          <span className="text-2xl font-bold text-green-600">
                            ${(analytics.revenue?.total || 0).toFixed(2)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">All time</p>
                      </div>
                      <div className="border-t pt-4">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-gray-600">Monthly Revenue</span>
                          <span className="text-xl font-bold text-blue-600">
                            ${(analytics.revenue?.monthly || 0).toFixed(2)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">Last 30 days</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">User Activity</h3>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-600">Active Users</span>
                          <span className="text-2xl font-bold text-primary-600">
                            {analytics.users?.active || 0}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full" 
                            style={{ 
                              width: `${analytics.users?.total > 0 
                                ? (analytics.users.active / analytics.users.total * 100) 
                                : 0}%` 
                            }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          {analytics.users?.total > 0 
                            ? ((analytics.users.active / analytics.users.total * 100).toFixed(1))
                            : 0}% of total users
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <TrendingUp size={20} className="text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-1">Analytics Dashboard</h4>
                      <p className="text-sm text-blue-700">
                        View real-time statistics about your platform's performance. More detailed analytics features coming soon.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <TrendingUp size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No analytics data available</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'system' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">System Administration</h2>
              <p className="text-gray-600 mt-1">Database backup, restore, and system maintenance tools</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Database Backup */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Shield className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Database Backup</h3>
                    <p className="text-sm text-gray-600">Create manual database backups</p>
                  </div>
                </div>
                <a
                  href={`https://dashit-io.onrender.com/backup-db.php?key=dashit-admin-backup-2024&token=${localStorage.getItem('token') || ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
                >
                  <Key size={16} />
                  Open Backup Tool
                </a>
              </div>

              {/* Database Restore */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingUp className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Database Restore</h3>
                    <p className="text-sm text-gray-600">Restore or merge from backup files</p>
                  </div>
                </div>
                <a
                  href={`https://dashit-io.onrender.com/restore-db.php?key=dashit-admin-backup-2024&token=${localStorage.getItem('token') || ''}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2"
                >
                  <TrendingUp size={16} />
                  Open Restore Tool
                </a>
              </div>

              {/* Safe Migration */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Package className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Safe Migration</h3>
                    <p className="text-sm text-gray-600">Schema updates without data loss</p>
                  </div>
                </div>
                <a
                  href={`${window.location.origin}/safe-migrate.php`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2"
                >
                  <Package size={16} />
                  Run Safe Migration
                </a>
              </div>

              {/* Debug Email Tool */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Package className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Debug Email Tool</h3>
                    <p className="text-sm text-gray-600">Test email fetching & pagination</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const token = localStorage.getItem('token')
                    if (!token) {
                      toast.error('Not authenticated')
                      return
                    }
                    // Open debug tool with token in URL
                    window.open(`https://dashit-io.onrender.com/debug-email.php?show_all=1&token=${encodeURIComponent(token)}`, '_blank')
                    toast.success('Opening debug tool...', { duration: 2000 })
                  }}
                  className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition flex items-center justify-center gap-2"
                >
                  <Package size={16} />
                  Open Email Debug Tool
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Note: Opens in new tab with authenticated session.
                </p>
              </div>

              {/* Debug Fitness Tool */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Debug Fitness Tool</h3>
                    <p className="text-sm text-gray-600">Check fitness data & database state</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const token = localStorage.getItem('token')
                    if (!token) {
                      toast.error('Not authenticated')
                      return
                    }
                    // Open debug tool with token in URL
                    window.open(`https://dashit-io.onrender.com/debug-fitness.php?token=${encodeURIComponent(token)}`, '_blank')
                    toast.success('Opening fitness debug tool...', { duration: 2000 })
                  }}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition flex items-center justify-center gap-2"
                >
                  <Users size={16} />
                  Open Fitness Debug Tool
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Requires admin authentication
                </p>
              </div>

              {/* System Status */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="text-purple-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">System Status</h3>
                    <p className="text-sm text-gray-600">View system health and logs</p>
                  </div>
                </div>
                <button
                  onClick={() => toast.success('System is running normally')}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition flex items-center justify-center gap-2"
                >
                  <CheckCircle size={16} />
                  Check Status
                </button>
              </div>
            </div>

            {/* Security Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <Shield className="text-yellow-600 mt-0.5" size={20} />
                <div>
                  <h4 className="font-semibold text-yellow-900 mb-2">Security Notice</h4>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    <li>• Database backup and restore operations require admin privileges AND a secret access key</li>
                    <li>• Debug tools require admin JWT authentication via Authorization header</li>
                    <li>• All operations are logged for audit purposes</li>
                    <li>• Backup files contain sensitive user data - store securely</li>
                    <li>• Restore operations will overwrite existing data</li>
                    <li>• Use safe migration for schema changes instead of force-schema</li>
                    <li>• Set BACKUP_SECRET_KEY environment variable for additional security</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Management Modal */}
      {selectedUserId && selectedUser && (
        <UserManagementModal
          user={selectedUser}
          isLoading={userLoading}
          onClose={() => setSelectedUserId(null)}
          onUpdate={(data) => updateUserMutation.mutate({ id: selectedUserId, data })}
          onSuspend={() => suspendUserMutation.mutate(selectedUserId)}
          onActivate={() => activateUserMutation.mutate(selectedUserId)}
          onDelete={() => {
            if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
              deleteUserMutation.mutate(selectedUserId)
            }
          }}
          onResetPassword={(newPassword) => {
            resetPasswordMutation.mutate({ id: selectedUserId, newPassword })
          }}
        />
      )}

      {/* Admin Management Modal */}
      {selectedAdminId && selectedAdminId > 0 && selectedAdmin && (
        <AdminManagementModal
          admin={selectedAdmin}
          isLoading={adminLoading}
          onClose={() => setSelectedAdminId(null)}
          onUpdate={(data) => updateAdminMutation.mutate({ id: selectedAdminId, data })}
          onDelete={() => {
            if (window.confirm('Are you sure you want to delete this admin? This action cannot be undone.')) {
              deleteAdminMutation.mutate(selectedAdminId)
            }
          }}
          onResetPassword={(newPassword) => {
            resetPasswordMutation.mutate({ id: selectedAdminId, newPassword })
          }}
        />
      )}

      {/* Create Admin Modal */}
      {selectedAdminId === -1 && (
        <CreateAdminModal
          onClose={() => setSelectedAdminId(null)}
          onCreate={(data) => createAdminMutation.mutate(data)}
        />
      )}

      {/* Reject App Modal */}
      {showRejectModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4"
            style={{
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-primary)',
            }}
          >
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between"
              style={{
                borderColor: 'var(--color-border)',
              }}
            >
              <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {activeTab === 'dev-applications' ? 'Reject Developer Application' : 'Reject App'}
              </h2>
              <button
                onClick={() => {
                  setShowRejectModal(null)
                  setRejectReason('')
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
                style={{
                  color: 'var(--color-text-secondary)',
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                {activeTab === 'dev-applications' 
                  ? 'Please provide a reason for rejecting this developer application. This will be sent to the user.'
                  : 'Please provide a reason for rejecting this app. This will be sent to the developer.'}
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g., App does not meet quality standards, violates terms of service, etc."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    if (!rejectReason.trim()) {
                      toast.error('Please provide a rejection reason')
                      return
                    }
                    if (activeTab === 'dev-applications') {
                      rejectDeveloperMutation.mutate({ applicationId: showRejectModal, reason: rejectReason })
                    } else {
                      rejectAppMutation.mutate({ id: showRejectModal, reason: rejectReason })
                    }
                  }}
                  disabled={(activeTab === 'dev-applications' ? rejectDeveloperMutation.isPending : rejectAppMutation.isPending) || !rejectReason.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(activeTab === 'dev-applications' ? rejectDeveloperMutation.isPending : rejectAppMutation.isPending) 
                    ? 'Rejecting...' 
                    : activeTab === 'dev-applications' ? 'Reject Application' : 'Reject App'}
                </button>
                <button
                  onClick={() => {
                    setShowRejectModal(null)
                    setRejectReason('')
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                  style={{
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Footer */}
      <DashboardFooter />
    </div>
  )
}

// User Management Modal Component
interface UserManagementModalProps {
  user: any
  isLoading: boolean
  onClose: () => void
  onUpdate: (data: any) => void
  onSuspend: () => void
  onActivate: () => void
  onDelete: () => void
  onResetPassword: (newPassword: string) => void
}

function UserManagementModal({
  user,
  isLoading,
  onClose,
  onUpdate,
  onSuspend,
  onActivate,
  onDelete,
  onResetPassword,
}: UserManagementModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [formData, setFormData] = useState({
    email: user.email || '',
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    role: user.role || 'user',
  })

  const modalRef = useModalClose(onClose)

  const handleResetPassword = () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    onResetPassword(newPassword)
    setNewPassword('')
    setShowPasswordReset(false)
  }

  const handleSave = () => {
    onUpdate(formData)
    setIsEditing(false)
  }

  if (isLoading) {
    return createPortal(
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text-primary)',
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10"
          style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'var(--color-border)',
          }}
        >
          <div>
            <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
              User Management
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              ID: {user.id} • Created: {new Date(user.created_at).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            style={{
              color: 'var(--color-text-secondary)',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Info Section */}
          <div className="bg-gray-50 rounded-xl p-6"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                User Information
              </h3>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
                >
                  <Edit2 size={16} />
                  Edit
                </button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    style={{
                      backgroundColor: 'var(--color-surface)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    style={{
                      backgroundColor: 'var(--color-surface)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    <option value="user">User</option>
                    <option value="developer">Developer</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    ⓘ Admin role can only be assigned through the Admin Management page
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false)
                      setFormData({
                        email: user.email || '',
                        first_name: user.first_name || '',
                        last_name: user.last_name || '',
                        role: user.role || 'user',
                      })
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                    style={{
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Name
                  </span>
                  <span className="text-base" style={{ color: 'var(--color-text-primary)' }}>
                    {user.first_name || user.last_name
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Email
                  </span>
                  <span className="text-base" style={{ color: 'var(--color-text-primary)' }}>
                    {user.email}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Role
                  </span>
                  <span className="px-3 py-1 text-xs bg-primary-100 text-primary-800 rounded-full capitalize">
                    {user.role}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Last Login
                  </span>
                  <span className="text-base" style={{ color: 'var(--color-text-primary)' }}>
                    {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Email Verified
                  </span>
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    user.email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {user.email_verified ? 'Verified' : 'Not Verified'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
              }}
            >
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Dashboards
              </div>
              <div className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {user.dashboard_count || 0}
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
              }}
            >
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Widgets
              </div>
              <div className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {user.widget_count || 0}
              </div>
            </div>
            <div className="bg-purple-50 p-4 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
              }}
            >
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Subscription
              </div>
              <div className="text-2xl font-bold capitalize" style={{ color: 'var(--color-text-primary)' }}>
                {user.plan_type || 'free'}
              </div>
            </div>
          </div>

          {/* Actions Section */}
          <div className="border-t pt-6"
            style={{
              borderColor: 'var(--color-border)',
            }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Account Actions
            </h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowPasswordReset(!showPasswordReset)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                <Key size={18} />
                Reset Password
              </button>
              {user.is_active ? (
                <button
                  onClick={onSuspend}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
                >
                  <Ban size={18} />
                  Suspend User
                </button>
              ) : (
                <button
                  onClick={onActivate}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                >
                  <CheckCircle size={18} />
                  Activate User
                </button>
              )}
              <button
                onClick={onDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              >
                <Trash2 size={18} />
                Delete User
              </button>
            </div>

            {/* Password Reset Section */}
            {showPasswordReset && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3">Reset User Password</h4>
                <div className="flex gap-3">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleResetPassword}
                    disabled={!newPassword}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Set Password
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordReset(false)
                      setNewPassword('')
                    }}
                    className="px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 transition"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  This will immediately change the user's password. They will need to use the new password to log in.
                </p>
              </div>
            )}

            <p className="text-sm mt-3" style={{ color: 'var(--color-text-secondary)' }}>
              ⚠️ Suspending a user will prevent them from logging in. Deleting a user is permanent and cannot be undone.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Admin Management Modal Component
interface AdminManagementModalProps {
  admin: any
  isLoading: boolean
  onClose: () => void
  onUpdate: (data: any) => void
  onDelete: () => void
  onResetPassword: (newPassword: string) => void
}

function AdminManagementModal({
  admin,
  isLoading,
  onClose,
  onUpdate,
  onDelete,
  onResetPassword,
}: AdminManagementModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [formData, setFormData] = useState({
    email: admin.email || '',
    first_name: admin.first_name || '',
    last_name: admin.last_name || '',
  })

  const modalRef = useModalClose(onClose)

  const handleResetPassword = () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    onResetPassword(newPassword)
    setNewPassword('')
    setShowPasswordReset(false)
  }

  const handleSave = () => {
    onUpdate(formData)
    setIsEditing(false)
  }

  if (isLoading) {
    return createPortal(
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        style={{
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text-primary)',
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10"
          style={{
            backgroundColor: 'var(--color-surface)',
            borderColor: 'var(--color-border)',
          }}
        >
          <div>
            <div className="flex items-center gap-2">
              <Shield size={24} className="text-red-600" />
              <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                Admin Management
              </h2>
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              ID: {admin.id} • Created: {new Date(admin.created_at).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            style={{
              color: 'var(--color-text-secondary)',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Admin Info Section */}
          <div className="bg-red-50 rounded-xl p-6"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Admin Information
              </h3>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                >
                  <Edit2 size={16} />
                  Edit
                </button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    style={{
                      backgroundColor: 'var(--color-surface)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false)
                      setFormData({
                        email: admin.email || '',
                        first_name: admin.first_name || '',
                        last_name: admin.last_name || '',
                      })
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                    style={{
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Name
                  </span>
                  <span className="text-base" style={{ color: 'var(--color-text-primary)' }}>
                    {admin.first_name || admin.last_name
                      ? `${admin.first_name || ''} ${admin.last_name || ''}`.trim()
                      : 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Email
                  </span>
                  <span className="text-base" style={{ color: 'var(--color-text-primary)' }}>
                    {admin.email}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Role
                  </span>
                  <span className="px-3 py-1 text-xs bg-red-100 text-red-800 rounded-full capitalize flex items-center gap-1 w-fit">
                    <Shield size={12} />
                    Admin
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Last Login
                  </span>
                  <span className="text-base" style={{ color: 'var(--color-text-primary)' }}>
                    {admin.last_login ? new Date(admin.last_login).toLocaleString() : 'Never'}
                  </span>
                </div>
                <div>
                  <span className="text-sm font-medium block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                    Email Verified
                  </span>
                  <span className={`px-3 py-1 text-xs rounded-full ${
                    admin.email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {admin.email_verified ? 'Verified' : 'Not Verified'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
              }}
            >
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Dashboards
              </div>
              <div className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {admin.dashboard_count || 0}
              </div>
            </div>
            <div className="bg-green-50 p-4 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
              }}
            >
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Widgets
              </div>
              <div className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {admin.widget_count || 0}
              </div>
            </div>
            <div className="bg-red-50 p-4 rounded-xl"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
              }}
            >
              <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Account Type
              </div>
              <div className="text-2xl font-bold capitalize" style={{ color: 'var(--color-text-primary)' }}>
                Admin
              </div>
            </div>
          </div>

          {/* Actions Section */}
          <div className="border-t pt-6"
            style={{
              borderColor: 'var(--color-border)',
            }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>
              Admin Actions
            </h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowPasswordReset(!showPasswordReset)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                <Key size={18} />
                Reset Password
              </button>
              <button
                onClick={onDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              >
                <Trash2 size={18} />
                Delete Admin
              </button>
            </div>

            {/* Password Reset Section */}
            {showPasswordReset && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3">Reset Admin Password</h4>
                <div className="flex gap-3">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleResetPassword}
                    disabled={!newPassword}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Set Password
                  </button>
                  <button
                    onClick={() => {
                      setShowPasswordReset(false)
                      setNewPassword('')
                    }}
                    className="px-4 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100 transition"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  This will immediately change the admin's password. They will need to use the new password to log in.
                </p>
              </div>
            )}

            <p className="text-sm mt-3" style={{ color: 'var(--color-text-secondary)' }}>
              ⚠️ Deleting an admin is permanent and cannot be undone. Make sure you have at least one active admin before deleting this account.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

// Create Admin Modal Component
interface CreateAdminModalProps {
  onClose: () => void
  onCreate: (data: any) => void
}

function CreateAdminModal({ onClose, onCreate }: CreateAdminModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
  })

  const modalRef = useModalClose(onClose)

  const handleCreate = () => {
    if (!formData.email || !formData.password) {
      toast.error('Email and password are required')
      return
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    onCreate({ ...formData, role: 'admin' })
    onClose()
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4"
        style={{
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text-primary)',
        }}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between"
          style={{
            borderColor: 'var(--color-border)',
          }}
        >
          <div>
            <div className="flex items-center gap-2">
              <Shield size={24} className="text-red-600" />
              <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                Create New Admin
              </h2>
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Create a new admin account with full system access
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            style={{
              color: 'var(--color-text-secondary)',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield size={20} className="text-red-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-900 mb-1">Security Warning</h4>
                <p className="text-sm text-red-700">
                  Admin accounts have full access to all system features, including user management, billing, and settings.
                  Only create admin accounts for trusted individuals.
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
                placeholder="admin@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)',
                }}
                placeholder="Minimum 6 characters"
                required
              />
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                Minimum 6 characters
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleCreate}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
            >
              Create Admin Account
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
              style={{
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

